import _ from 'lodash';
import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import Modal from 'common/components/ModalComponent';
import MoneyDisplay from 'common/components/MoneyDisplay';
import Multiplier from 'ui/Strategies/components/Strategy/Multiplier';
import ErrorMesssage from 'common/components/ErrorMesssage';
import { cyclicPlansPeriods } from 'ui/Plans/config';

const propTypes = {
  onChangeSelectedPlan: PropTypes.func.isRequired,
  planName: PropTypes.string,
  onShowPurchaseTypeModal: PropTypes.func.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  isPremium: PropTypes.bool.isRequired,
  isPro: PropTypes.bool.isRequired,
  plansGroupedByCyclicMonths: PropTypes.oneOfType([PropTypes.array, PropTypes.shape({})])
};

const defaultProps = {
  planName: '',
  plansGroupedByCyclicMonths: {}
};

const PlanMultiplierModal = ({
  onChangeSelectedPlan,
  planName,
  onShowPurchaseTypeModal,
  onCloseModal,
  isPremium,
  isPro,
  plansGroupedByCyclicMonths
}) => {
  const [multiplier, setMultiplier] = useState(1);
  const { yearly: { period: yearlyPeriod } } = cyclicPlansPeriods;
  const getYearlyOnlyPlans = _.omitBy(
    _.mapValues(
      _.pickBy(plansGroupedByCyclicMonths, (plans, key) => parseInt(key, 10) % yearlyPeriod === 0),
      (plans) => _.head(_.filter(plans, { is_premium: isPremium, is_pro: isPro })) || null
    ),
    _.isNull
  );

  const selectedPlan = getYearlyOnlyPlans[multiplier * yearlyPeriod];

  const renderPlanDetails = () => {
    const {
      currentNetPrice,
      historic_run_limit: historicRunLimit,
      live_run_limit: liveRunLimit,
      cycle_duration_in_months: cycleMonth
    } = selectedPlan;
    return (
      <>
        {liveRunLimit > 0 && (
        <div className="mt-2">
          Validity:
          {' '}
          <b>
            {cycleMonth}
            {' '}
            month
          </b>
        </div>
        )}
        {historicRunLimit > 0 && (
        <div className="mt-2">
          Backtest credits:
          {' '}
          <b>{historicRunLimit}</b>
        </div>
        )}
        {liveRunLimit > 0 && (
        <div className="mt-2">
          Live Deployment credits:
          {' '}
          <b>{liveRunLimit}</b>
        </div>
        )}
        <div className="my-2 d-flex justify-content-center align-items-center">
          Total
          {' '}
          {multiplier}
          {' '}
          Year
          {' '}
          {planName}
          {' '}
          Plan Cost:
          <b>
            <MoneyDisplay>{currentNetPrice}</MoneyDisplay>
          </b>
        </div>
      </>
    );
  };

  const MULTIPLIER_LIMIT = Object.keys(getYearlyOnlyPlans)?.length || 1;

  const isInValidMultiplier = useMemo(() => multiplier > MULTIPLIER_LIMIT, [multiplier]);

  return (
    <Modal
      onSave={() => {
        onChangeSelectedPlan(selectedPlan);
        onShowPurchaseTypeModal();
      }}
      onClose={onCloseModal}
      size="md"
      okBtnTitle={`Purchase ${planName} Plan`}
      isOkBtnDisabled={isInValidMultiplier || !selectedPlan}
      btnClassName="btn-sm"
    >
      <div className="text-center mb-2">
        <div className=" icon-info alert-info border-radius-14 mb-3 width-fit-content m-auto text-center">
        <i className="fa-regular fa-circle-info tx-30 "></i>
        </div>
        <h4 className="font-weight-medium mb-3">{`${planName} Yearly Plan Multiplier`}</h4>
        <p>Number of Years: </p>
        <Multiplier
          multiplier={multiplier}
          onChangeMultiplier={setMultiplier}
          maxMultiplier={MULTIPLIER_LIMIT}
        />
        { !isInValidMultiplier && selectedPlan && renderPlanDetails()}
        {isInValidMultiplier && (
          <ErrorMesssage
            error={{
              message: `Plans are only available up to ${MULTIPLIER_LIMIT} years`
            }}
          />
        )}
      </div>
    </Modal>
  );
};

PlanMultiplierModal.propTypes = propTypes;
PlanMultiplierModal.defaultProps = defaultProps;

export default PlanMultiplierModal;
